/**
 * MerchantOnboardingAgGridHook
 *
 * Grid for /admin/merchants/onboarding. Backed by MerchantCore.Onboarding,
 * a live query against the external MMS database (PlatformCore.ShukriaMmsRepo)
 * that used to cap at a flat 500 rows with no way to see past them — this
 * uses AG Grid's server-side row model so the page fetches one page at a
 * time over the LiveView socket instead.
 */

import { mountServerSideGrid } from "../base/ServerSideAgGridHook";

const PAGE_SIZE = 25;

const STATUS_COLORS = {
  1: { bg: "#DCFCE7", color: "#166534" }, // active
  2: { bg: "#FEF3C7", color: "#92400E" }, // paused
  0: { bg: "#FEE2E2", color: "#991B1B" }, // suspended
};

const CONTRACT_COLORS = {
  completed: { bg: "#DCFCE7", color: "#166534" },
  initiated: { bg: "#FEF3C7", color: "#92400E" },
  failed: { bg: "#FEE2E2", color: "#991B1B" },
};

const pill = (bg, color, label) =>
  `<span style="background:${bg};color:${color};padding:1px 8px;border-radius:9999px;font-size:11px;font-weight:600;white-space:nowrap;">${label}</span>`;

const statusCell = (params) => {
  if (!params.data) return "";
  const meta = STATUS_COLORS[params.value] || { bg: "var(--ag-chip-background-color)", color: "var(--ag-data-color)" };
  return pill(meta.bg, meta.color, params.data.status_label || String(params.value));
};

const contractStatusCell = (params) => {
  if (!params.value) return '<span style="color:var(--ag-secondary-foreground-color);font-size:11px;">N/A</span>';
  const meta = CONTRACT_COLORS[params.value] || { bg: "var(--ag-chip-background-color)", color: "var(--ag-data-color)" };
  const label = params.value.charAt(0).toUpperCase() + params.value.slice(1);
  return pill(meta.bg, meta.color, label);
};

const mccCell = (params) => {
  if (!params.data) return "";
  if (!params.value) return pill("#FEF3C7", "#92400E", "No MCC");
  const desc = params.data.mcc_description;
  return `<div>
    ${pill("#DBEAFE", "#1E40AF", params.value)}
    ${desc ? `<div style="font-size:10px;color:var(--ag-secondary-foreground-color);margin-top:2px;">${desc}</div>` : ""}
  </div>`;
};

const MerchantOnboardingAgGridHook = {
  mounted() {
    if (!window.agGrid || !window.agGrid.createGrid) {
      console.error("MerchantOnboardingAgGridHook: agGrid not found on window");
      return;
    }

    const columnDefs = [
      { headerName: "Name", field: "name", flex: 1.2, minWidth: 160 },
      { headerName: "Email", field: "email", flex: 1.2, minWidth: 180 },
      { headerName: "MCC", colId: "mcc", width: 150, sortable: false, cellRenderer: mccCell },
      { headerName: "Status", field: "status", width: 120, cellRenderer: statusCell },
      { headerName: "Contract Status", field: "contract_status", width: 150, cellRenderer: contractStatusCell },
      { headerName: "Registered At", field: "created_at", width: 170 },
      {
        headerName: "Action",
        colId: "action",
        pinned: "right",
        width: 130,
        sortable: false,
        filter: false,
        floatingFilter: false,
        resizable: false,
        cellRenderer: (params) => {
          if (!params.data || !params.data.mms_url) return "";
          return `<a href="${params.data.mms_url}" target="_blank" rel="noopener noreferrer" style="color:#4F46E5;font-size:12px;text-decoration:none;">View in MMS ↗</a>`;
        },
      },
    ];

    this.gridApi = mountServerSideGrid(this, {
      columnDefs,
      rowsEvent: "merchant_onboarding:rows",
      refreshEvent: "merchant_onboarding:refresh",
      pageSize: PAGE_SIZE,
    });
  },

  destroyed() {
    if (this.gridApi) {
      this.gridApi.destroy();
      this.gridApi = null;
    }
  },
};

export default MerchantOnboardingAgGridHook;
